import AppModal from '@common/ui/AppModal';
import { LabeledTextField, MaterialIcon } from '@components';
import { useGlobalNotifier } from '@hooks';
import { useCreateSavedInventoryQuery } from '@Inventory/services/savedQueryQueries';
import { LoadingButton } from '@mui/lab';
import { Alert, AlertTitle, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import InventoryCounts from './InventoryCounts';
import { InventoryPageQueryKeys } from '@Inventory/common/queryKeys';
import { routes } from '@routes';
import { NEW_ACCESS_FLOW_QUERY_PARAM_VALUE, WITH_SAVED_QUERY_QUERY_PARAM } from '@AccessFlows/common/constants';
import { CustomAnalyticsEvents, useAnalyticsContext } from '@utils/analytics';
import PermissionButton from '@common/components/PermissionButton';
import { Permission } from '@common/utils/admin_permissions';

export function SaveInventoryQueryModal({ open }: { open: boolean }) {
  const [queryParams, setQueryParams] = useSearchParams();

  const onClose = () => {
    const newQueryParams = new URLSearchParams(queryParams);

    newQueryParams.delete(InventoryPageQueryKeys.SaveQueryModalOpen);
    setQueryParams(newQueryParams);
  };

  const ModalHeader = (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
      <MaterialIcon symbol="auto_fix_high" weight={700} size="medium" />
      <Typography variant="subtitle1">Save Access Scope</Typography>
    </Stack>
  );
  return (
    <AppModal open={open} onClose={onClose} header={ModalHeader} width={800}>
      <SaveInventoryQueryModalInner onClose={onClose} />
    </AppModal>
  );
}

function SaveInventoryQueryModalInner({ onClose }: { onClose: () => void }) {
  const [queryParams, setQueryParams] = useSearchParams();
  const { track } = useAnalyticsContext();

  const query = queryParams.get(InventoryPageQueryKeys.Query);
  const fromAccessFlow = queryParams.get(WITH_SAVED_QUERY_QUERY_PARAM);

  const [name, setName] = useState('');
  const navigate = useNavigate();

  const {
    createSavedInventoryQuery,
    isLoading: isCreateInventoryQueryLoading,
    inventoryQueryId,
  } = useCreateSavedInventoryQuery();

  const { notifySuccess } = useGlobalNotifier();

  useEffect(() => {
    if (inventoryQueryId) {
      notifySuccess(`Saved access scope ${name} created`);
      track(CustomAnalyticsEvents.INVENTORY_ACCESS_SCOPE_CREATED_SUCCESSFULLY);

      if (fromAccessFlow) {
        let accessFlowFormUrl = routes.AddAccessFlow.path;
        if (fromAccessFlow !== NEW_ACCESS_FLOW_QUERY_PARAM_VALUE) {
          accessFlowFormUrl = generatePath(routes.EditAccessFlow.path, { id: fromAccessFlow });
        }

        const editLinkQP = new URLSearchParams();
        editLinkQP.set(WITH_SAVED_QUERY_QUERY_PARAM, inventoryQueryId);
        navigate(`${accessFlowFormUrl}?${editLinkQP.toString()}`, { replace: true });
        return;
      }

      const newQp = new URLSearchParams(queryParams);
      newQp.set(InventoryPageQueryKeys.SavedQueryId, inventoryQueryId);
      newQp.delete(InventoryPageQueryKeys.SaveQueryModalOpen);
      setQueryParams(newQp);
    }
  }, [inventoryQueryId, name, notifySuccess, fromAccessFlow, navigate, queryParams, setQueryParams, track]);

  return (
    <Stack direction="column" spacing={2}>
      <Alert variant="outlinedScope" icon={<MaterialIcon symbol="info" size="small" />}>
        <Stack direction="column" spacing={1}>
          <AlertTitle>
            You are about to save a list of <InventoryCounts type="resources" noText /> resources and{' '}
            <InventoryCounts type="permissions" noText /> permissions.
          </AlertTitle>
        </Stack>
        Please note that any changes in the environment may affect these resources (e.g., adding or removing resources
        that match these filters).
      </Alert>
      <Stack width={300}>
        <LabeledTextField
          label="Access Scope Name"
          value={name}
          maxRows={1}
          onChange={(e) => setName(e.target.value)}
          fullWidth={false}
        />
      </Stack>
      <Stack direction={'row'} justifyContent="space-between" spacing={2}>
        <Button variant="outlined" onClick={onClose}>
          Go Back
        </Button>
        <PermissionButton
          onClick={() => createSavedInventoryQuery({ name, query: query || '' })}
          loading={isCreateInventoryQueryLoading}
          variant="contained"
          color="primary"
          disabled={name === ''}
          permissions={[Permission.INVENTORY_WRITE]}
        >
          {fromAccessFlow ? 'Save Access Scope And Return to Access Flow Builder' : 'Save Access Scope'}
        </PermissionButton>
      </Stack>
    </Stack>
  );
}
